import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, FileText, Settings2, Package, Wrench, Users as UsersIcon, Ban, Upload, Download, Eye, Paperclip, Search } from 'lucide-react';
import { SortableHeader } from '@/components/SortableHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MaterialPOs() {
  const { token, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pos, setPOs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [editingPO, setEditingPO] = useState(null);
  const [deletingPO, setDeletingPO] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [nextPoNumber, setNextPoNumber] = useState('');
  const [filterJob, setFilterJob] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const [formData, setFormData] = useState({
    jobId: '',
    date: new Date().toISOString().split('T')[0],
    vendorName: '',
    type: 'MATERIAL',
    approxAmount: '',
    notes: '',
    invoiceReceivedDate: '',
    actualAmount: '',
    status: 'PENDING',
    isLoggedInMaterialLog: false
  });

  const [poSettings, setPOSettings] = useState({
    poNumberPrefix: 'MPO',
    poNumberNextSequence: 1
  });

  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [selectedPOForAttachment, setSelectedPOForAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);

  const isOffice = user?.role === 'OFFICE' || user?.role === 'OWNER';
  const isForeman = user?.role === 'FOREMAN';

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-open edit dialog when ?edit= query param is present
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && pos.length > 0) {
      const poToEdit = pos.find(p => p.id === editId);
      if (poToEdit) {
        handleOpenDialog(poToEdit);
        setSearchParams({}, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos, searchParams]);

  const fetchData = async () => {
    try {
      const [posRes, jobsRes, nextNumRes] = await Promise.all([
        axios.get(`${API}/material-pos`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/jobs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/material-pos/next-number`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setPOs(posRes.data);
      setJobs(jobsRes.data);
      setNextPoNumber(nextNumRes.data.nextPoNumber);

      // Get settings if office
      if (isOffice) {
        try {
          const settingsRes = await axios.get(`${API}/settings`, { headers: { Authorization: `Bearer ${token}` } });
          setPOSettings({
            poNumberPrefix: settingsRes.data.poNumberPrefix || 'MPO',
            poNumberNextSequence: settingsRes.data.poNumberNextSequence || 1
          });
        } catch (e) {
          console.warn('Settings not loaded:', e?.message);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load Material POs');
    } finally {
      setLoading(false);
    }
  };

  const getJobInfo = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    return job ? { jobNumber: job.jobNumber, description: job.description } : { jobNumber: jobId, description: '' };
  };

  const handleOpenDialog = (po = null) => {
    if (po) {
      setEditingPO(po);
      setFormData({
        jobId: po.jobId,
        date: po.date,
        vendorName: po.vendorName,
        type: po.type,
        approxAmount: po.approxAmount.toString(),
        notes: po.notes || '',
        invoiceReceivedDate: po.invoiceReceivedDate || '',
        actualAmount: po.actualAmount?.toString() || '',
        status: po.status,
        isLoggedInMaterialLog: po.isLoggedInMaterialLog,
        poNumber: po.poNumber || ''
      });
    } else {
      setEditingPO(null);
      setFormData({
        jobId: '',
        date: new Date().toISOString().split('T')[0],
        vendorName: '',
        type: 'MATERIAL',
        approxAmount: '',
        notes: '',
        invoiceReceivedDate: '',
        actualAmount: '',
        status: 'PENDING',
        isLoggedInMaterialLog: false
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingPO) {
        // Office updating existing PO
        const updateData = {
          jobId: formData.jobId,
          date: formData.date,
          vendorName: formData.vendorName,
          type: formData.type,
          approxAmount: parseFloat(formData.approxAmount),
          notes: formData.notes || null,
          invoiceReceivedDate: formData.invoiceReceivedDate || null,
          actualAmount: formData.actualAmount ? parseFloat(formData.actualAmount) : null,
          status: formData.status,
          isLoggedInMaterialLog: formData.isLoggedInMaterialLog
        };
        // Include poNumber if changed
        if (formData.poNumber && formData.poNumber !== editingPO.poNumber) {
          updateData.poNumber = formData.poNumber;
        }
        await axios.patch(`${API}/material-pos/${editingPO.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Material PO updated');
      } else {
        // Creating new PO
        const createData = {
          jobId: formData.jobId,
          date: formData.date,
          vendorName: formData.vendorName,
          type: formData.type,
          approxAmount: parseFloat(formData.approxAmount),
          notes: formData.notes || null
        };
        await axios.post(`${API}/material-pos`, createData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Material PO created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save Material PO:', error);
      toast.error(error.response?.data?.detail || 'Failed to save Material PO');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingPO) return;

    try {
      await axios.delete(`${API}/material-pos/${deletingPO.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Material PO deleted');
      setDeleteDialogOpen(false);
      setDeletingPO(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete Material PO:', error);
      toast.error('Failed to delete Material PO');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.patch(`${API}/settings/po-sequence`, poSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('PO sequence settings updated');
      setSettingsDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update settings');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedPOForAttachment) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/material-pos/${selectedPOForAttachment.id}/attachments`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Invoice PDF uploaded successfully');
      fetchData();
      // Refresh the selected PO
      const updatedPO = pos.find(p => p.id === selectedPOForAttachment.id);
      if (updatedPO) {
        setSelectedPOForAttachment({ ...updatedPO, attachments: [...(updatedPO.attachments || []), { filename: file.name }] });
      }
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleViewAttachment = async (poId, attachmentId, filename) => {
    try {
      const response = await axios.get(`${API}/material-pos/${poId}/attachments/${attachmentId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create blob URL and open in new tab
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Failed to view attachment:', error);
      toast.error('Failed to open PDF');
    }
  };

  const handleDeleteAttachment = async (poId, attachmentId) => {
    try {
      await axios.delete(`${API}/material-pos/${poId}/attachments/${attachmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Attachment deleted');
      fetchData();
      // Refresh selected PO
      if (selectedPOForAttachment?.id === poId) {
        setSelectedPOForAttachment(prev => ({
          ...prev,
          attachments: prev.attachments?.filter(a => a.id !== attachmentId) || []
        }));
      }
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      toast.error('Failed to delete attachment');
    }
  };

  const openAttachmentDialog = (po) => {
    setSelectedPOForAttachment(po);
    setAttachmentDialogOpen(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'MATERIAL':
        return <Package className='w-4 h-4' />;
      case 'EQUIPMENT':
        return <Wrench className='w-4 h-4' />;
      case 'SUBCONTRACTOR':
        return <UsersIcon className='w-4 h-4' />;
      case 'NON_BILLABLE':
        return <Ban className='w-4 h-4' />;
      default:
        return <FileText className='w-4 h-4' />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'MATERIAL':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'EQUIPMENT':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'SUBCONTRACTOR':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'NON_BILLABLE':
        return 'bg-slate-100 text-slate-600 border-slate-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'INVOICED':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'LOGGED':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  // Filter POs
  const handleSort = (column) => {
    if (sortColumn === column) setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(column); setSortDir('asc'); }
  };

  const filteredPOs = pos.filter(po => {
    if (filterJob !== 'all' && po.jobId !== filterJob) return false;
    if (filterStatus !== 'all' && po.status !== filterStatus) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      if (!(po.poNumber?.toLowerCase().includes(q) || po.vendorName?.toLowerCase().includes(q) || getJobNumber(po.jobId)?.toLowerCase().includes(q))) return false;
    }
    return true;
  }).sort((a, b) => {
    if (!sortColumn) return 0;
    let aVal, bVal;
    switch (sortColumn) {
      case 'poNumber': aVal = a.poNumber || ''; bVal = b.poNumber || ''; break;
      case 'date': aVal = a.date || ''; bVal = b.date || ''; break;
      case 'jobId': aVal = getJobNumber(a.jobId); bVal = getJobNumber(b.jobId); break;
      case 'vendorName': aVal = a.vendorName || ''; bVal = b.vendorName || ''; break;
      case 'type': aVal = a.type || ''; bVal = b.type || ''; break;
      case 'approxAmount': aVal = a.approxAmount || 0; bVal = b.approxAmount || 0; break;
      case 'actualAmount': aVal = a.actualAmount || 0; bVal = b.actualAmount || 0; break;
      case 'status': aVal = a.status || ''; bVal = b.status || ''; break;
      default: return 0;
    }
    if (typeof aVal === 'number') return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    return sortDir === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
  });

  // Calculate totals
  const totalApprox = filteredPOs.reduce((sum, po) => sum + po.approxAmount, 0);
  const totalActual = filteredPOs.reduce((sum, po) => sum + (po.actualAmount || 0), 0);

  if (loading) {
    return (
      <div className='p-4 md:p-8'>
        <div className='text-center text-slate-600'>Loading Material POs...</div>
      </div>
    );
  }

  return (
    <div className='p-4 md:p-8' data-testid='material-pos-page'>
      <div className='flex flex-col gap-4 mb-6'>
        <div>
          <h1 className='text-2xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900'>Material Purchase Orders</h1>
          <p className='mt-1 md:mt-2 text-base md:text-lg text-slate-700'>
            {isForeman ? 'Create POs for material orders' : 'Manage material purchase orders'}
          </p>
        </div>
        <div className='flex flex-col sm:flex-row gap-2'>
          {isOffice && (
            <Button
              onClick={() => setSettingsDialogOpen(true)}
              variant='outline'
              className='border-2 border-slate-200 hover:border-[#1A3668] rounded-sm py-3 md:py-2'
            >
              <Settings2 className='w-4 h-4 mr-2' />
              PO Settings
            </Button>
          )}
          <Button
            onClick={() => handleOpenDialog()}
            data-testid='create-po-button'
            className='bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm py-4 md:py-2 text-base active:scale-[0.98]'
          >
            <Plus className='w-5 h-5 mr-2' />
            New PO
          </Button>
        </div>
      </div>

      {/* Filters - Mobile Scrollable */}
      <div className='flex gap-3 mb-6 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0'>
        <div className='flex-shrink-0'>
          <Label className='text-xs text-slate-500'>Job</Label>
          <Select value={filterJob} onValueChange={setFilterJob}>
            <SelectTrigger className='w-[150px] md:w-[200px] rounded-sm border-2'>
              <SelectValue placeholder='All Jobs' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Jobs</SelectItem>
              {jobs.map((job) => (
                <SelectItem key={job.id} value={job.id}>{job.jobNumber}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className='flex-shrink-0'>
          <Label className='text-xs text-slate-500'>Status</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className='w-[130px] md:w-[150px] rounded-sm border-2'>
              <SelectValue placeholder='All Status' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Status</SelectItem>
              <SelectItem value='PENDING'>Pending</SelectItem>
              <SelectItem value='INVOICED'>Invoiced</SelectItem>
              <SelectItem value='LOGGED'>Logged</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className='grid grid-cols-3 gap-2 md:gap-4 mb-6'>
        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-3 md:p-4'>
            <p className='text-xs md:text-sm font-bold uppercase tracking-wider text-slate-500'>Total POs</p>
            <p className='text-xl md:text-3xl font-bold text-[#1A3668] mt-1 md:mt-2'>{filteredPOs.length}</p>
          </CardContent>
        </Card>
        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-3 md:p-4'>
            <p className='text-xs md:text-sm font-bold uppercase tracking-wider text-slate-500'>Approx Amt</p>
            <p className='text-lg md:text-3xl font-bold text-amber-600 mt-1 md:mt-2'>{formatCurrency(totalApprox)}</p>
          </CardContent>
        </Card>
        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-3 md:p-4'>
            <p className='text-xs md:text-sm font-bold uppercase tracking-wider text-slate-500'>Actual Amt</p>
            <p className='text-lg md:text-3xl font-bold text-[#16A34A] mt-1 md:mt-2'>{formatCurrency(totalActual)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      {pos.length > 0 && (
        <div className='mb-4'>
          <div className='relative max-w-xs'>
            <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
            <Input data-testid='po-search' value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder='Search POs...' className='pl-9 rounded-sm border-2' />
          </div>
        </div>
      )}

      {/* PO List - Mobile Cards / Desktop Table */}
      {filteredPOs.length === 0 ? (
        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-8 md:p-12 text-center'>
            <FileText className='w-10 md:w-12 h-10 md:h-12 mx-auto text-slate-400 mb-4' />
            <p className='text-slate-600 mb-4'>No Material POs found</p>
            <Button
              onClick={() => handleOpenDialog()}
              variant='outline'
              className='border-2 border-[#1A3668] text-[#1A3668] hover:bg-slate-50 rounded-sm py-3'
            >
              Create your first PO
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className='md:hidden space-y-3'>
            {filteredPOs.map((po) => (
              <Card key={po.id} data-testid={`po-card-${po.id}`} className='border-slate-200 shadow-sm rounded-sm'>
                <CardContent className='p-4'>
                  <div className='flex justify-between items-start mb-3'>
                    <div>
                      <p className='font-mono font-bold text-[#1A3668]'>{po.poNumber}</p>
                      <p className='text-sm text-slate-600'>{po.date}</p>
                    </div>
                    <div className='flex flex-col items-end gap-1'>
                      <Badge className={`rounded-sm text-xs ${getStatusColor(po.status)}`}>
                        {po.status}
                      </Badge>
                      <Badge className={`rounded-sm flex items-center gap-1 text-xs ${getTypeColor(po.type)}`}>
                        {getTypeIcon(po.type)}
                        <span>{po.type.replace('_', ' ')}</span>
                      </Badge>
                    </div>
                  </div>
                  <div className='mb-3'>
                    <p className='font-medium text-slate-900'>{po.vendorName}</p>
                    <p className='text-sm text-[#1A3668]'>{getJobInfo(po.jobId).jobNumber}</p>
                  </div>
                  <div className='grid grid-cols-2 gap-2 bg-slate-50 rounded-sm p-2 mb-3'>
                    <div>
                      <p className='text-xs text-slate-500'>Approx</p>
                      <p className='font-mono font-bold text-amber-600'>{formatCurrency(po.approxAmount)}</p>
                    </div>
                    <div>
                      <p className='text-xs text-slate-500'>Actual</p>
                      <p className='font-mono font-bold text-[#16A34A]'>
                        {po.actualAmount ? formatCurrency(po.actualAmount) : '-'}
                      </p>
                    </div>
                  </div>
                  <div className='flex items-center justify-between text-xs text-slate-500 mb-3'>
                    <span>By: {po.requestedByName}</span>
                    <div className='flex items-center gap-2'>
                      {po.attachments?.length > 0 && (
                        <Badge className='bg-blue-100 text-blue-800 rounded-sm text-xs flex items-center gap-1'>
                          <Paperclip className='w-3 h-3' />
                          {po.attachments.length} PDF
                        </Badge>
                      )}
                      {po.isLoggedInMaterialLog ? (
                        <Badge className='bg-green-100 text-green-800 rounded-sm text-xs'>Logged</Badge>
                      ) : null}
                    </div>
                  </div>
                  {/* Attachment button - visible to all */}
                  <Button
                    onClick={() => openAttachmentDialog(po)}
                    variant='outline'
                    size='sm'
                    className='w-full mb-2 border-2 border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300 rounded-sm'
                  >
                    <Paperclip className='w-3 h-3 mr-1' />
                    {po.attachments?.length > 0 ? `View Invoices (${po.attachments.length})` : 'Invoices'}
                  </Button>
                  {isOffice && (
                    <div className='flex gap-2'>
                      <Button
                        onClick={() => handleOpenDialog(po)}
                        data-testid={`edit-po-${po.id}`}
                        variant='outline'
                        size='sm'
                        className='flex-1 border-2 border-slate-200 hover:border-[#1A3668] rounded-sm'
                      >
                        <Pencil className='w-3 h-3 mr-1' />
                        Edit
                      </Button>
                      <Button
                        onClick={() => {
                          setDeletingPO(po);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-po-${po.id}`}
                        variant='outline'
                        size='sm'
                        className='border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-sm'
                      >
                        <Trash2 className='w-3 h-3' />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className='hidden md:block overflow-x-auto'>
            <table className='w-full border-collapse' data-testid='material-pos-table'>
              <thead>
                <tr className='bg-slate-100 border-b-2 border-slate-200'>
                  <SortableHeader column='poNumber' label='PO #' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} className='!p-3' />
                  <SortableHeader column='date' label='Date' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} className='!p-3' />
                  <SortableHeader column='jobId' label='Job' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} className='!p-3' />
                  <SortableHeader column='vendorName' label='Vendor' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} className='!p-3' />
                  <SortableHeader column='type' label='Type' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} className='!p-3' />
                  <SortableHeader column='approxAmount' label='Approx Amt' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} className='!p-3 text-right' />
                  <SortableHeader column='actualAmount' label='Actual Amt' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} className='!p-3 text-right' />
                  <th className='text-left p-3 text-sm font-bold uppercase tracking-wider text-slate-700'>Requested By</th>
                  <SortableHeader column='status' label='Status' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} className='!p-3 text-center' />
                  <th className='text-center p-3 text-sm font-bold uppercase tracking-wider text-slate-700'>Invoices</th>
                  <th className='text-center p-3 text-sm font-bold uppercase tracking-wider text-slate-700'>Logged</th>
                  {isOffice && <th className='text-center p-3 text-sm font-bold uppercase tracking-wider text-slate-700'>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filteredPOs.map((po) => (
                  <tr key={po.id} data-testid={`po-row-${po.id}`} className='border-b border-slate-200 hover:bg-slate-50'>
                    <td className='p-3 text-sm font-mono font-bold text-[#1A3668]'>{po.poNumber}</td>
                    <td className='p-3 text-sm text-slate-600'>{po.date}</td>
                    <td className='p-3 text-sm font-medium text-slate-700'>{getJobInfo(po.jobId).jobNumber}</td>
                    <td className='p-3 text-sm text-slate-700'>{po.vendorName}</td>
                    <td className='p-3'>
                      <Badge className={`rounded-sm flex items-center gap-1 w-fit ${getTypeColor(po.type)}`}>
                        {getTypeIcon(po.type)}
                        <span className='text-xs'>{po.type.replace('_', ' ')}</span>
                      </Badge>
                    </td>
                    <td className='p-3 text-right text-sm font-mono text-amber-600'>{formatCurrency(po.approxAmount)}</td>
                    <td className='p-3 text-right text-sm font-mono text-[#16A34A]'>
                      {po.actualAmount ? formatCurrency(po.actualAmount) : '-'}
                    </td>
                    <td className='p-3 text-sm text-slate-600'>{po.requestedByName}</td>
                    <td className='p-3 text-center'>
                      <Badge className={`rounded-sm ${getStatusColor(po.status)}`}>
                        {po.status}
                      </Badge>
                    </td>
                    <td className='p-3 text-center'>
                      <Button
                        onClick={() => openAttachmentDialog(po)}
                        variant='outline'
                        size='sm'
                        className={`rounded-sm ${po.attachments?.length > 0 ? 'border-blue-300 text-blue-600 bg-blue-50' : 'border-slate-200'}`}
                      >
                        <Paperclip className='w-3 h-3 mr-1' />
                        {po.attachments?.length > 0 ? po.attachments.length : 0}
                      </Button>
                    </td>
                    <td className='p-3 text-center'>
                      {po.isLoggedInMaterialLog ? (
                        <Badge className='bg-green-100 text-green-800 rounded-sm'>Yes</Badge>
                      ) : (
                        <Badge className='bg-slate-100 text-slate-500 rounded-sm'>No</Badge>
                      )}
                    </td>
                    {isOffice && (
                      <td className='p-3 text-center'>
                        <div className='flex gap-2 justify-center'>
                          <Button
                            onClick={() => handleOpenDialog(po)}
                            data-testid={`edit-po-${po.id}`}
                            variant='outline'
                            size='sm'
                            className='border-2 border-slate-200 hover:border-[#1A3668] rounded-sm'
                          >
                            <Pencil className='w-3 h-3' />
                          </Button>
                          <Button
                            onClick={() => {
                              setDeletingPO(po);
                              setDeleteDialogOpen(true);
                            }}
                            data-testid={`delete-po-${po.id}`}
                            variant='outline'
                            size='sm'
                            className='border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-sm'
                          >
                            <Trash2 className='w-3 h-3' />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className='sm:max-w-[700px] rounded-sm max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='text-2xl font-bold'>
              {editingPO ? 'Edit Material PO' : 'New Material PO'}
            </DialogTitle>
            <DialogDescription>
              {editingPO ? 'Update purchase order details' : `Next PO Number: ${nextPoNumber}`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className='space-y-4 py-4'>
              <div>
                <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>Job *</Label>
                <Select
                  value={formData.jobId}
                  onValueChange={async (value) => {
                    setFormData({ ...formData, jobId: value });
                    // Update PO number preview with customer-specific prefix
                    if (!editingPO) {
                      try {
                        const res = await axios.get(`${API}/material-pos/next-number?jobId=${value}`, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        setNextPoNumber(res.data.nextPoNumber);
                      } catch (err) {
                        console.error('Failed to fetch next PO number:', err);
                      }
                    }
                  }}
                  disabled={editingPO && !isOffice}
                >
                  <SelectTrigger data-testid='po-job-select' className='mt-2 rounded-sm border-2'>
                    <SelectValue placeholder='Select job' />
                  </SelectTrigger>
                  <SelectContent>
                    {jobs.map((job) => (
                      <SelectItem key={job.id} value={job.id}>
                        {job.jobNumber} - {job.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>Date *</Label>
                  <Input
                    type='date'
                    data-testid='po-date-input'
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    className='mt-2 rounded-sm border-2'
                    disabled={editingPO && !isOffice}
                  />
                </div>
                <div>
                  <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>Type *</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => setFormData({ ...formData, type: value })}
                    disabled={editingPO && !isOffice}
                  >
                    <SelectTrigger data-testid='po-type-select' className='mt-2 rounded-sm border-2'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='MATERIAL'>Material</SelectItem>
                      <SelectItem value='EQUIPMENT'>Equipment</SelectItem>
                      <SelectItem value='SUBCONTRACTOR'>Subcontractor</SelectItem>
                      <SelectItem value='NON_BILLABLE'>Non-Billable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>Vendor Name *</Label>
                  <Input
                    data-testid='vendor-name-input'
                    value={formData.vendorName}
                    onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
                    placeholder='Steel Supply Co'
                    required
                    className='mt-2 rounded-sm border-2'
                    disabled={editingPO && !isOffice}
                  />
                </div>
                <div>
                  <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>Approx Amount *</Label>
                  <Input
                    type='number'
                    step='0.01'
                    data-testid='approx-amount-input'
                    value={formData.approxAmount}
                    onChange={(e) => setFormData({ ...formData, approxAmount: e.target.value })}
                    placeholder='5000.00'
                    required
                    className='mt-2 rounded-sm border-2'
                    disabled={editingPO && !isOffice}
                  />
                </div>
              </div>

              <div>
                <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>Notes</Label>
                <Textarea
                  data-testid='po-notes-input'
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder='Description of materials or work...'
                  className='mt-2 rounded-sm border-2'
                  rows={3}
                  disabled={editingPO && !isOffice}
                />
              </div>

              {/* Office-only fields for updating */}
              {isOffice && editingPO && (
                <>
                  <div className='border-t border-slate-200 pt-4 mt-4'>
                    <h4 className='text-sm font-bold uppercase tracking-wider text-slate-500 mb-4'>Invoice Details (Office Only)</h4>
                    
                    <div className='mb-4'>
                      <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>PO Number</Label>
                      <Input
                        data-testid='po-number-input'
                        value={formData.poNumber || ''}
                        onChange={(e) => setFormData({ ...formData, poNumber: e.target.value })}
                        placeholder='e.g. ACME-0001'
                        className='mt-2 rounded-sm border-2'
                      />
                    </div>

                    <div className='grid grid-cols-2 gap-4'>
                      <div>
                        <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>Invoice Received Date</Label>
                        <Input
                          type='date'
                          data-testid='invoice-received-date'
                          value={formData.invoiceReceivedDate}
                          onChange={(e) => setFormData({ ...formData, invoiceReceivedDate: e.target.value })}
                          className='mt-2 rounded-sm border-2'
                        />
                      </div>
                      <div>
                        <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>Actual Amount</Label>
                        <Input
                          type='number'
                          step='0.01'
                          data-testid='actual-amount-input'
                          value={formData.actualAmount}
                          onChange={(e) => setFormData({ ...formData, actualAmount: e.target.value })}
                          placeholder='Actual invoice amount'
                          className='mt-2 rounded-sm border-2'
                        />
                      </div>
                    </div>

                    <div className='grid grid-cols-2 gap-4 mt-4'>
                      <div>
                        <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>Status</Label>
                        <Select
                          value={formData.status}
                          onValueChange={(value) => setFormData({ ...formData, status: value })}
                        >
                          <SelectTrigger data-testid='po-status-select' className='mt-2 rounded-sm border-2'>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='PENDING'>Pending</SelectItem>
                            <SelectItem value='INVOICED'>Invoiced</SelectItem>
                            <SelectItem value='LOGGED'>Logged</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className='flex items-center gap-2 mt-8'>
                        <Checkbox
                          id='isLogged'
                          checked={formData.isLoggedInMaterialLog}
                          onCheckedChange={(checked) => setFormData({ ...formData, isLoggedInMaterialLog: checked })}
                        />
                        <Label htmlFor='isLogged' className='text-sm font-medium text-slate-700'>
                          Logged in Material Log
                        </Label>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button type='button' variant='outline' onClick={() => setDialogOpen(false)} className='border-2 border-slate-200 rounded-sm'>
                Cancel
              </Button>
              <Button
                type='submit'
                data-testid='submit-po-button'
                disabled={submitting}
                className='bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm'
              >
                {submitting ? 'Saving...' : editingPO ? 'Update' : 'Create PO'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className='rounded-sm'>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Material PO</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete PO <strong>{deletingPO?.poNumber}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className='rounded-sm'>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid='confirm-delete-po'
              className='bg-red-600 hover:bg-red-700 rounded-sm'
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PO Settings Dialog (Office Only) */}
      {isOffice && (
        <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
          <DialogContent className='sm:max-w-[400px] rounded-sm'>
            <DialogHeader>
              <DialogTitle className='text-2xl font-bold'>PO Number Settings</DialogTitle>
              <DialogDescription>
                Configure the PO number format and starting sequence
              </DialogDescription>
            </DialogHeader>
            <div className='space-y-4 py-4'>
              <div>
                <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>PO Number Prefix</Label>
                <Input
                  value={poSettings.poNumberPrefix}
                  onChange={(e) => setPOSettings({ ...poSettings, poNumberPrefix: e.target.value })}
                  placeholder='MPO'
                  className='mt-2 rounded-sm border-2'
                />
                <p className='text-xs text-slate-500 mt-1'>Example: {poSettings.poNumberPrefix}-0001</p>
              </div>
              <div>
                <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>Next Sequence Number</Label>
                <Input
                  type='number'
                  value={poSettings.poNumberNextSequence}
                  onChange={(e) => setPOSettings({ ...poSettings, poNumberNextSequence: parseInt(e.target.value) || 1 })}
                  min='1'
                  className='mt-2 rounded-sm border-2'
                />
              </div>
            </div>
            <DialogFooter>
              <Button type='button' variant='outline' onClick={() => setSettingsDialogOpen(false)} className='border-2 border-slate-200 rounded-sm'>
                Cancel
              </Button>
              <Button
                onClick={handleSaveSettings}
                className='bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm'
              >
                Save Settings
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Attachment Dialog */}
      <Dialog open={attachmentDialogOpen} onOpenChange={setAttachmentDialogOpen}>
        <DialogContent className='sm:max-w-[500px]'>
          <DialogHeader>
            <DialogTitle className='text-xl font-bold text-[#1A3668]'>
              Invoice Documents
            </DialogTitle>
            <DialogDescription>
              {selectedPOForAttachment && (
                <span>
                  PO: <strong>{selectedPOForAttachment.poNumber}</strong> - {selectedPOForAttachment.vendorName}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className='space-y-4 py-4'>
            {/* Upload Section - Office Only */}
            {isOffice && (
              <div className='border-2 border-dashed border-slate-300 rounded-sm p-4 text-center'>
                <input
                  type='file'
                  id='pdf-upload'
                  accept='.pdf'
                  onChange={handleFileUpload}
                  className='hidden'
                  disabled={uploading}
                />
                <label
                  htmlFor='pdf-upload'
                  className={`cursor-pointer flex flex-col items-center gap-2 ${uploading ? 'opacity-50' : ''}`}
                >
                  <Upload className='w-8 h-8 text-slate-400' />
                  <span className='text-sm text-slate-600'>
                    {uploading ? 'Uploading...' : 'Click to upload invoice PDF'}
                  </span>
                  <span className='text-xs text-slate-400'>Max file size: 10MB</span>
                </label>
              </div>
            )}

            {/* Attachments List */}
            <div className='space-y-2'>
              <Label className='text-sm font-bold uppercase tracking-wider text-slate-500'>
                Attached Invoices ({selectedPOForAttachment?.attachments?.length || 0})
              </Label>
              
              {(!selectedPOForAttachment?.attachments || selectedPOForAttachment.attachments.length === 0) ? (
                <div className='text-center py-6 bg-slate-50 rounded-sm'>
                  <FileText className='w-10 h-10 mx-auto text-slate-300 mb-2' />
                  <p className='text-sm text-slate-500'>No invoices attached yet</p>
                </div>
              ) : (
                <div className='space-y-2'>
                  {selectedPOForAttachment.attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className='flex items-center justify-between p-3 bg-slate-50 rounded-sm border border-slate-200'
                    >
                      <div className='flex items-center gap-3 flex-1 min-w-0'>
                        <FileText className='w-8 h-8 text-red-500 flex-shrink-0' />
                        <div className='min-w-0'>
                          <p className='font-medium text-slate-900 truncate'>{attachment.filename}</p>
                          <p className='text-xs text-slate-500'>
                            Uploaded {new Date(attachment.uploadedAt).toLocaleDateString()} by {attachment.uploadedByName}
                          </p>
                        </div>
                      </div>
                      <div className='flex items-center gap-2 flex-shrink-0'>
                        <Button
                          onClick={() => handleViewAttachment(selectedPOForAttachment.id, attachment.id, attachment.filename)}
                          variant='outline'
                          size='sm'
                          className='border-blue-200 text-blue-600 hover:bg-blue-50 rounded-sm'
                        >
                          <Eye className='w-4 h-4' />
                        </Button>
                        {isOffice && (
                          <Button
                            onClick={() => handleDeleteAttachment(selectedPOForAttachment.id, attachment.id)}
                            variant='outline'
                            size='sm'
                            className='border-red-200 text-red-600 hover:bg-red-50 rounded-sm'
                          >
                            <Trash2 className='w-4 h-4' />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={() => setAttachmentDialogOpen(false)}
              className='bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm'
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
